import { EventCanGoLive, Pool, TicketBody, TicketDetailsParams } from './types';
import { getEventPools, updateTicket } from './services';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { NEW_TICKET } from './utils';
import { ROUTES } from 'features/routes';
import { camelizeKeys } from 'humps';
import { createTicket } from 'features/CreateTicket/services';
import { getData } from 'services/api';
import { queryClient } from 'index';
import { camelize, sendGAEvent } from 'utils/utils';
import { uploadImage } from 'services/UploadImage/uploadImage';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';

export const useTicketTypeApi = () => {
	const { push } = useHistory();
	const { t } = useTranslation();
	const { id: eventId, ticketId } = useParams<TicketDetailsParams>();
	const { showNotification, TYPES } = useToastNotification();

	const isNewTicket = location.pathname.includes(NEW_TICKET);

	const { data: ticket } = useQuery({
		queryKey: ['ticket', ticketId],
		queryFn: (): Promise<TicketBody> => getData(`/ticket_types/${ticketId}`),
		enabled: !isNewTicket,
	});

	const { data: canGoLive } = useQuery({
		queryKey: ['canGoLive', eventId],
		queryFn: (): Promise<EventCanGoLive> => {
			const searchParams = new URLSearchParams([['set_live', 'false']]);
			return getData(`/events/${eventId}/go-live?` + searchParams.toString());
		},
		select: (data) => camelizeKeys(data) as EventCanGoLive,
		enabled: !!eventId,
	});

	const { data: pools } = useQuery({
		queryKey: ['eventPools', eventId],
		queryFn: (): Promise<Pool[]> => getEventPools(Number(eventId)),
		enabled: !!eventId,
		select: (data) => camelize(data) as Pool[],
	});

	const { mutateAsync: mutateUploadImage } = useMutation(uploadImage, {
		onError: () => {
			showNotification({
				message: t('error-uploading-image'),
				type: TYPES.ERROR,
			});
		},
	});

	const { mutate: mutateCreateTicket } = useMutation(createTicket, {
		onSuccess: () => {
			sendGAEvent({ category: 'Journeys', action: 'Create ticket' });
			push(ROUTES.DASHBOARD_PATH.replace(':id', eventId));
		},
	});

	const { mutate: mutateUpdateTicket } = useMutation(updateTicket, {
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
			queryClient.invalidateQueries({ queryKey: ['event', eventId] });

			sendGAEvent({ category: 'Journeys', action: 'Edit ticket' });
			push(ROUTES.DASHBOARD_PATH.replace(':id', eventId));
		},
	});

	return { ticket, canGoLive, pools, mutateUploadImage, mutateCreateTicket, mutateUpdateTicket };
};
